/**
 * useCustomerContracts Hook
 * Ügyfél szerződések kezelése
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { CustomerContract, ContractFormData, ContractStats } from '../types/customerContract';
import {
  createContract as createContractService,
  updateContract as updateContractService,
  deleteContract as deleteContractService,
  getContract,
  getCustomerContracts,
  getContractStats,
  updateHourPool,
  resetHourPool,
  addTaskToContract,
  addInvoiceToContract
} from '../services/customerContractService';
import { toast } from 'react-hot-toast';

export const useCustomerContracts = (customerId?: string) => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState<CustomerContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ContractStats | null>(null);

  // Real-time listener
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    let q;
    if (customerId) {
      // Egy ügyfél szerződései
      q = query(
        collection(db, 'customerContracts'),
        where('organizationId', '==', user.organizationId),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Összes szerződés
      q = query(
        collection(db, 'customerContracts'),
        where('organizationId', '==', user.organizationId),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const contractsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Timestamp konverzió
            startDate: data.startDate instanceof Timestamp ? data.startDate.toDate() : data.startDate,
            endDate: data.endDate instanceof Timestamp ? data.endDate.toDate() : data.endDate,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
            nextBillingDate: data.nextBillingDate instanceof Timestamp ? data.nextBillingDate.toDate() : data.nextBillingDate,
            lastBillingDate: data.lastBillingDate instanceof Timestamp ? data.lastBillingDate.toDate() : data.lastBillingDate
          } as CustomerContract;
        });
        
        setContracts(contractsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching contracts:', error);
        toast.error('Hiba a szerződések betöltésekor');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId, customerId]);

  // Statisztikák betöltése
  useEffect(() => {
    if (!user?.organizationId || customerId) return;

    const loadStats = async () => {
      try {
        const statsData = await getContractStats(user.organizationId);
        setStats(statsData);
      } catch (error) {
        console.error('Error loading contract stats:', error);
      }
    };

    loadStats();
  }, [user?.organizationId, customerId, contracts.length]);

  // Szerződés létrehozása
  const createContract = async (data: ContractFormData): Promise<string> => {
    if (!user?.organizationId || !user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      const contractId = await createContractService(
        data,
        user.organizationId,
        user.uid,
        user.displayName || user.email || 'Unknown'
      );
      toast.success('Szerződés sikeresen létrehozva');
      return contractId;
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error('Hiba a szerződés létrehozásakor');
      throw error;
    }
  };

  // Szerződés frissítése
  const updateContract = async (
    contractId: string,
    data: Partial<ContractFormData>
  ): Promise<void> => {
    try {
      await updateContractService(contractId, data);
      toast.success('Szerződés sikeresen frissítve');
    } catch (error) {
      console.error('Error updating contract:', error);
      toast.error('Hiba a szerződés frissítésekor');
      throw error;
    }
  };

  // Szerződés törlése
  const deleteContract = async (contractId: string): Promise<void> => {
    try {
      await deleteContractService(contractId);
      toast.success('Szerződés sikeresen lezárva');
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error('Hiba a szerződés lezárásakor');
      throw error;
    }
  };

  // Órakeret frissítése
  const updateContractHourPool = async (
    contractId: string,
    serviceIndex: number,
    hoursUsed: number
  ): Promise<void> => {
    try {
      await updateHourPool(contractId, serviceIndex, hoursUsed);
      toast.success('Órakeret frissítve');
    } catch (error) {
      console.error('Error updating hour pool:', error);
      toast.error('Hiba az órakeret frissítésekor');
      throw error;
    }
  };

  // Órakeret nullázása
  const resetContractHourPool = async (
    contractId: string,
    serviceIndex: number
  ): Promise<void> => {
    try {
      await resetHourPool(contractId, serviceIndex);
      toast.success('Órakeret nullázva');
    } catch (error) {
      console.error('Error resetting hour pool:', error);
      toast.error('Hiba az órakeret nullázásakor');
      throw error;
    }
  };

  // Task hozzáadása
  const linkTaskToContract = async (
    contractId: string,
    taskId: string
  ): Promise<void> => {
    try {
      await addTaskToContract(contractId, taskId);
      toast.success('Task hozzáadva a szerződéshez');
    } catch (error) {
      console.error('Error linking task to contract:', error);
      toast.error('Hiba a task hozzáadásakor');
      throw error;
    }
  };

  // Számla hozzáadása
  const linkInvoiceToContract = async (
    contractId: string,
    invoiceId: string
  ): Promise<void> => {
    try {
      await addInvoiceToContract(contractId, invoiceId);
      toast.success('Számla hozzáadva a szerződéshez');
    } catch (error) {
      console.error('Error linking invoice to contract:', error);
      toast.error('Hiba a számla hozzáadásakor');
      throw error;
    }
  };

  return {
    contracts,
    loading,
    stats,
    createContract,
    updateContract,
    deleteContract,
    updateContractHourPool,
    resetContractHourPool,
    linkTaskToContract,
    linkInvoiceToContract
  };
};
